import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Category } from '../../../models/entities/accounting/category';
import { Equity } from '../../../models/entities/accounting/equity';
import { FixedAsset } from '../../../models/entities/accounting/fixed-asset';
import { Liability } from '../../../models/entities/accounting/liability';
import { PayableExpenseCategory } from '../../../models/entities/payable-expense-category';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { CategoryPickerComponent } from '../category-picker/category-picker.component';
import { StringValidatorsModule } from '../../../directives/validators/string/string-validators.module';

/**
 * Manages expense allocation across multiple accounting categories for payables with split accounting.
 *
 * Enforces allocation constraints where split amounts must exactly equal the total payable amount
 * to prevent accounting discrepancies. Supports both single-category (simplified) and multi-category
 * (split) expense tracking with automatic amount synchronization when in single-category mode.
 *
 * Key constraint: When multiple categories exist, the sum must equal the total within 0.01 tolerance
 * to account for currency rounding. Duplicate categories are prevented to maintain GL integrity.
 */
@UntilDestroy()
@Component({
    selector: 'app-expense-categories-form',
    templateUrl: './expense-categories-form.component.html',
    styleUrls: ['./expense-categories-form.component.scss'], standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        CategoryPickerComponent,
        StringValidatorsModule
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ExpenseCategoriesFormComponent implements OnInit, OnChanges {
    @Input() parentFormGroup: UntypedFormGroup;
    // SD-7271/7407/7474: org to load categories for. Passed through to <app-category-select> so the
    // picker resolves the payable's org instead of falling back to the token org (cross-org 404 fix).
    @Input() organizationId: number = null;
    @Input() hasAccrualAccounting = false;
    @Input() totalAmount = 0;
    @Input() invalidControls: string[] = [];
    @Input() readonly = false;
    @Input() defaultCategory: Category | Equity | FixedAsset | Liability = null;
    @Input() existingExpenseCategories: PayableExpenseCategory[] = [];

    @Output() expenseCategoriesLoadingChange = new EventEmitter<boolean>();
    @Output() validationStateChange = new EventEmitter<{ isValid: boolean, errorMessage?: string }>();

    expenseCategoriesLoading = false;
    validationError: string = null;

    constructor(private _formBuilder: UntypedFormBuilder) {}

    get expenseCategories(): UntypedFormArray {
        return this.parentFormGroup?.get('expenseCategories') as UntypedFormArray;
    }

    get isSingleCategoryReadonly(): boolean {
        return this.expenseCategories?.length === 1 || this.readonly;
    }

    /** One category is the invoice total and needs no chrome; two or more are a split. */
    get isSplit(): boolean {
        return this.expenseCategories?.length > 1;
    }

    ngOnInit(): void {
        this.initializeComponent();
        this.setupFormSubscriptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.parentFormGroup) {
            this.ensureExpenseCategoriesArrayExists();
            this.initializeComponent();
            this.setupFormSubscriptions();
        }

        if (changes.existingExpenseCategories) {
            this.handleExistingExpenseCategories();
        }

        if (changes.totalAmount) {
            this.updateExpenseAmountsForTotalChange();
            this.validateCategories();
        }

        if (changes.defaultCategory && !changes.defaultCategory.firstChange) {
            this.handleDefaultCategoryChange();
        }
    }

    /**
     * Splits expense allocation between categories, using floor/ceil division to handle odd amounts.
     *
     * When converting from single to dual category, preserves the existing category and splits
     * the amount 50/50 using floor/ceil to ensure the sum exactly equals the original amount.
     * For subsequent splits, adds zero-amount categories requiring manual allocation.
     */
    addSplit(): void {
        const splitAmount = this.totalAmount ? [Math.floor(this.totalAmount / 2), Math.ceil(this.totalAmount / 2)] : [0, 0];
        const firstCategory = this.expenseCategories.at(0)?.get('expenseCategory')?.value;

        if (this.expenseCategories.length === 1) {
            this.expenseCategories.at(0).setValue({ expenseCategoryAmount: splitAmount[0], expenseCategory: firstCategory });
            this.expenseCategories.push(this.createSplit(null, splitAmount[1]));
        } else {
            this.expenseCategories.push(this.createSplit(null, 0));
        }

        this.expenseCategories.updateValueAndValidity();
    }

    removeSplit(index: number): void {
        this.expenseCategories?.removeAt(index);
        this.expenseCategories.updateValueAndValidity();

        if (this.expenseCategories.length === 1) {
            this.updateSingleSplitAmount();
        }
    }

    onExpenseCategoriesLoadingStart(): void {
        this.expenseCategoriesLoading = true;
        this.expenseCategoriesLoadingChange.emit(this.expenseCategoriesLoading);
    }

    onExpenseCategoriesLoadingEnd(): void {
        this.expenseCategoriesLoading = false;
        this.expenseCategoriesLoadingChange.emit(this.expenseCategoriesLoading);
    }

    /**
     * Validates allocation integrity with 0.01 tolerance for currency rounding errors.
     *
     * Single-category expenses bypass validation as amounts auto-sync. Multi-category splits
     * must sum to exactly the total amount (within penny tolerance) and use unique categories
     * to prevent duplicate GL entries that would distort financial reporting.
     */
    validateCategories(): boolean {
        this.validationError = null;
        if (!this.expenseCategories || this.expenseCategories.length <= 1 || !this.totalAmount) {
            this.emitValidationState(true);
            return true;
        }

        const categorySum = this.expenseCategories.controls.reduce((sum, control) => sum + (control.get('expenseCategoryAmount')?.value || 0) / 100, 0);
        const totalAmount = this.totalAmount / 100;

        if (Math.abs(categorySum - totalAmount) > 0.01) {
            this.validationError = `The sum of category amounts ($${categorySum.toFixed(2)}) must equal the total amount ($${totalAmount.toFixed(2)})`;
            this.emitValidationState(false, this.validationError);
            return false;
        }

        const seen = new Set<number>();
        for (const control of this.expenseCategories.controls) {
            const categoryId = control.get('expenseCategory')?.value?.id;
            if (categoryId && seen.has(categoryId)) {
                this.validationError = 'Expense categories must be unique';
                this.emitValidationState(false, this.validationError);
                return false;
            }
            seen.add(categoryId);
        }

        this.emitValidationState(true);
        return true;
    }

    private setupFormSubscriptions(): void {
        if (!this.parentFormGroup) { return; }

        const amountControl = this.parentFormGroup.get('amount');
        if (amountControl) {
            amountControl.valueChanges.pipe(
                untilDestroyed(this),
                filter(() => this.expenseCategories?.length === 1),
                distinctUntilChanged()
            ).subscribe(() => {
                this.updateSingleSplitAmount();
                this.validateCategories();
            });
        }

        this.expenseCategories.valueChanges.pipe(
            untilDestroyed(this),
            debounceTime(100),
            distinctUntilChanged()
        ).subscribe(() => this.validateCategories());

        const categoryAmountChanges = this.expenseCategories.controls.map(control =>
            control.get('expenseCategoryAmount').valueChanges.pipe(
                startWith(control.get('expenseCategoryAmount').value)
            )
        );

        if (categoryAmountChanges.length > 0) {
            combineLatest(categoryAmountChanges).pipe(
                untilDestroyed(this),
                debounceTime(100),
                distinctUntilChanged()
            ).subscribe(() => this.validateCategories());
        }
    }

    private ensureExpenseCategoriesArrayExists(): void {
        if (!this.parentFormGroup?.get('expenseCategories')) {
            this.parentFormGroup.addControl('expenseCategories', this._formBuilder.array([]));
        }
    }

    private initializeWithExpenseCategories(expenseCategories: PayableExpenseCategory[]): void {
        this.expenseCategories.clear();
        expenseCategories.forEach((category) => {
            const expenseCategory = this.makeExpenseCategory(category.expenseCategory || (category as any).expense_category);
            const amount = category.amount || category.expenseCategoryAmount || (category as any).expense_category_amount || 0;
            this.expenseCategories.push(this.createSplit(expenseCategory, amount));
        });
        this.syncLoneLineToInvoice();
        this.expenseCategories.updateValueAndValidity();
    }

    /** SD-12158: one category is the whole bill; a line amount read off the invoice would leave it short and read-only. */
    private syncLoneLineToInvoice(): void {
        const invoiceAmount = Number(this.parentFormGroup?.get('amount')?.value);
        if (this.expenseCategories.length === 1 && invoiceAmount > 0) {
            this.expenseCategories.at(0).get('expenseCategoryAmount')?.setValue(invoiceAmount);
        }
    }

    private initializeWithDefaultCategory(): void {
        this.expenseCategories.clear();
        this.expenseCategories.push(this.createSplit(this.makeExpenseCategory(this.defaultCategory), this.totalAmount || 0));
        this.expenseCategories.updateValueAndValidity();
    }

    /**
     * Converts raw category data to typed accounting entities based on GL account type.
     *
     * PayHOA's chart of accounts supports expense categories plus asset/liability/equity accounts
     * for capital purchases and loan allocations. Account type determines GL posting behavior.
     */
    private makeExpenseCategory(category: any): Category | Equity | FixedAsset | Liability {
        if (!category) {
            return null;
        }
        switch (category.accountType) {
            case 'asset': return new FixedAsset(category);
            case 'liability': return new Liability(category);
            case 'equity': return new Equity(category);
            case 'category': return new Category(category);
            default: return null;
        }
    }

    private createSplit(category: any = null, amount: number = 0): UntypedFormGroup {
        return this._formBuilder.group({
            expenseCategory: [category, Validators.required],
            expenseCategoryAmount: [amount, [Validators.required, Validators.min(0)]],
        });
    }

    private updateExpenseAmountsForTotalChange(): void {
        if (this.expenseCategories?.length === 1) {
            this.updateSingleSplitAmount();
        }
    }

    private updateSingleSplitAmount(): void {
        const amount = this.parentFormGroup?.get('amount')?.value || this.totalAmount || 0;
        this.expenseCategories.at(0)?.get('expenseCategoryAmount')?.setValue(amount);
    }

    private emitValidationState(isValid: boolean, errorMessage?: string): void {
        this.validationStateChange.emit({ isValid, errorMessage });
    }

    private initializeComponent(): void {
        if (!this.parentFormGroup) {
            return;
        }

        this.ensureExpenseCategoriesArrayExists();

        const hasFormControls = this.expenseCategories?.length > 0 && this.expenseCategories.at(0) instanceof UntypedFormGroup;

        if (this.existingExpenseCategories?.length > 0 && !hasFormControls) {
            this.initializeWithExpenseCategories(this.existingExpenseCategories);
        } else if (this.expenseCategories?.length === 0) {
            this.initializeWithDefaultCategory();
        } else if (!hasFormControls) {
            this.convertRawDataToFormControls();
        }
    }

    private convertRawDataToFormControls(): void {
        const rawData = this.expenseCategories.value;
        this.expenseCategories.clear();
        rawData.forEach((item: any) => {
            const expenseCategory = this.makeExpenseCategory(item.expenseCategory || item.expense_category);
            const amount = item.amount || item.expenseCategoryAmount || item.expense_category_amount || 0;
            this.expenseCategories.push(this.createSplit(expenseCategory, amount));
        });
        this.syncLoneLineToInvoice();
        this.expenseCategories.updateValueAndValidity();
    }

    private handleExistingExpenseCategories(): void {
        const hasNewData = this.existingExpenseCategories?.length > 0;
        const currentFormHasData = this.expenseCategories?.length > 0;

        if (hasNewData && !currentFormHasData) {
            this.initializeWithExpenseCategories(this.existingExpenseCategories);
        } else if (hasNewData && currentFormHasData && this.shouldUpdateFormWithExistingData(this.expenseCategories.value, this.existingExpenseCategories)) {
            this.initializeWithExpenseCategories(this.existingExpenseCategories);
        }
    }

    /**
     * Updates expense category when the default category input changes (e.g., vendor selection).
     *
     * Auto-populates the vendor's default category when a vendor is selected on a payable.
     * Only updates when there's a single expense category row (not split) to preserve
     * intentional multi-category allocations. Matches the legacy LOB bill pay behavior.
     */
    private handleDefaultCategoryChange(): void {
        // Only update if there's exactly one expense category (not split)
        if (this.expenseCategories?.length !== 1) {
            return;
        }

        // Update the expense category with the new default
        if (this.defaultCategory) {
            this.expenseCategories.at(0).patchValue({
                expenseCategory: this.makeExpenseCategory(this.defaultCategory)
            });
        }
    }

    private shouldUpdateFormWithExistingData(currentFormData: any[], existingCategories: PayableExpenseCategory[]): boolean {
        if (!currentFormData?.length || !existingCategories?.length) {
            return !currentFormData?.length;
        }
        if (currentFormData.length !== existingCategories.length) {
            return existingCategories.length > 1 || currentFormData.length === 0;
        }
        if (currentFormData.length === 1 && existingCategories.length === 1) {
            const formItem = currentFormData[0];
            const existingItem = existingCategories[0];
            const existingCategory = existingItem.expenseCategory || (existingItem as any).expense_category;
            return existingCategory?.id !== formItem.expenseCategory?.id;
        }
        return false;
    }

    /** SD-12158: by row, not position, so removing a split never leaves another row's typed amount on screen. */
    trackBySplit(_index: number, split: AbstractControl): AbstractControl {
        return split;
    }
}
